/**
 * @file use-dev-mode.ts
 * @description 개발자 모드 체크 React Hook
 *
 * 클라이언트 컴포넌트에서 개발자 모드 여부를 확인하고 제어하는 커스텀 훅입니다.
 *
 * 사용 예시:
 * ```tsx
 * const { isDevMode, setDevMode, isLoading } = useDevMode();
 * if (isDevMode) {
 *   // 모든 기능 활성화
 * }
 * ```
 */

import { useState, useEffect, useCallback } from "react";
import { useIsDeveloper } from "./use-is-developer";
import {
  isDevMode as checkDevMode,
  setDevMode as setDevModeState,
  getDevModeState,
} from "@/lib/utils/dev-mode";

export function useDevMode() {
  const { isDeveloper, isLoading: isDeveloperLoading } = useIsDeveloper();
  const [devModeEnabled, setDevModeEnabled] = useState(false);
  const [mounted, setMounted] = useState(false);

  // 클라이언트 마운트 후 localStorage 읽기
  useEffect(() => {
    setMounted(true);
    if (typeof window !== "undefined") {
      const isDevelopment = process.env.NODE_ENV === "development";
      // 개발 서버: localStorage에 값이 없으면 기본값 true (스위치가 켜진 상태)
      // 프로덕션: localStorage 값 사용 (없으면 false)
      try {
        const stored = localStorage.getItem("bidix-dev-mode-enabled");
        let enabled: boolean;
        if (isDevelopment) {
          // 개발 서버: 값이 없으면 true (기본값), 있으면 그 값 사용
          enabled = stored === null ? true : stored === "true";
        } else {
          // 프로덕션: 값이 없으면 false, 있으면 그 값 사용
          enabled = stored === "true";
        }
        setDevModeEnabled(enabled);
        
        // 쿠키에도 동기화 (서버에서 확인 가능하도록)
        if (enabled) {
          document.cookie = `bidix-dev-mode=${enabled}; path=/; max-age=86400`; // 24시간
        } else {
          document.cookie = `bidix-dev-mode=; path=/; max-age=0`; // 삭제
        }
      } catch (error) {
        console.error("[useDevMode] localStorage 읽기 오류:", error);
        const enabled = isDevelopment ? true : false;
        setDevModeEnabled(enabled);
        if (enabled) {
          document.cookie = `bidix-dev-mode=${enabled}; path=/; max-age=86400`;
        }
      }
    }
  }, []);

  // 개발자 모드 상태 변경 이벤트 리스너
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleDevModeChange = (event: CustomEvent<{ enabled: boolean }>) => {
      const enabled = event.detail.enabled;
      setDevModeEnabled(enabled);
      
      // 쿠키에도 동기화
      if (enabled) {
        document.cookie = `bidix-dev-mode=${enabled}; path=/; max-age=86400`;
      } else {
        document.cookie = `bidix-dev-mode=; path=/; max-age=0`;
      }
    };

    window.addEventListener(
      "dev-mode-changed",
      handleDevModeChange as EventListener,
    );
    return () => {
      window.removeEventListener(
        "dev-mode-changed",
        handleDevModeChange as EventListener,
      );
    };
  }, []);

  // 개발자 모드 활성화 여부 계산
  // 개발 서버: devModeEnabled 상태 직접 사용
  // 프로덕션: 개발자 계정이고 devModeEnabled가 true일 때만 활성화
  const isDevelopment = process.env.NODE_ENV === "development";
  const isDevMode = mounted && (
    isDevelopment 
      ? devModeEnabled  // 개발 서버: 상태 직접 사용
      : (isDeveloper && devModeEnabled)  // 프로덕션: 개발자 계정 + 스위치 활성화
  );

  // 개발자 모드 스위치 토글
  const toggleDevMode = useCallback(() => {
    // 개발 서버에서는 개발자 계정 체크 없이 토글 가능
    const isDevelopment = process.env.NODE_ENV === "development";
    if (!isDevelopment && !isDeveloper) return;

    const newState = !devModeEnabled;
    setDevModeState(newState);
    setDevModeEnabled(newState);
    
    // 쿠키에 개발자 모드 상태 저장 (서버에서 확인 가능하도록)
    if (typeof document !== "undefined") {
      if (newState) {
        document.cookie = `bidix-dev-mode=${newState}; path=/; max-age=86400`; // 24시간
      } else {
        document.cookie = `bidix-dev-mode=; path=/; max-age=0`; // 삭제
      }
    }
  }, [isDeveloper, devModeEnabled]);

  // 개발자 모드 스위치 설정
  const setDevMode = useCallback(
    (enabled: boolean) => {
      // 개발 서버에서는 개발자 계정 체크 없이 설정 가능
      const isDevelopment = process.env.NODE_ENV === "development";
      if (!isDevelopment && !isDeveloper) return;

      setDevModeState(enabled);
      setDevModeEnabled(enabled);
      
      // 쿠키에 개발자 모드 상태 저장 (서버에서 확인 가능하도록)
      if (typeof document !== "undefined") {
        if (enabled) {
          document.cookie = `bidix-dev-mode=${enabled}; path=/; max-age=86400`; // 24시간
        } else {
          document.cookie = `bidix-dev-mode=; path=/; max-age=0`; // 삭제
        }
      }
    },
    [isDeveloper],
  );

  return {
    isDevMode,
    devModeEnabled, // 스위치 상태 (개발자 계정일 때만 의미 있음)
    setDevMode,
    toggleDevMode,
    isDeveloper,
    isLoading: isDeveloperLoading || !mounted,
  };
}
